import { useState, useEffect } from "react";
import { Table, Tag, Spin, Empty } from "antd";
import { StockOutlined } from "@ant-design/icons";
import { Store } from "lucide-react";
import { getEventInventory } from "services/portalEvents.service";
import { renderErrorNotifications } from "helpers/error.helpers";

const columns = [
  {
    title: "Product",
    dataIndex: "product_name",
    align:"center",
    key: "product_name",
    render: (name) => <span style={{ fontWeight: 600 }}>{name}</span>,
  },
  {
    title: "Total",
    dataIndex: "total_quantity",
    key: "total_quantity",
    align: "center",
    render: (v) => (
      <Tag
        color="blue"
        style={{ minWidth: 48, textAlign: "center", fontSize: 13 }}
      >
        {v ?? "—"}
      </Tag>
    ),
  },
  {
    title: "Sold",
    dataIndex: "used_quantity",
    key: "used_quantity",
    align: "center",
    render: (v) => (
      <Tag
        color="green"
        style={{ minWidth: 48, textAlign: "center", fontSize: 13 }}
      >
        {v ?? 0}
      </Tag>
    ),
  },
  {
    title: "Reserved",
    dataIndex: "reserved_quantity",
    key: "reserved_quantity",
    align: "center",
    render: (v) => (
      <Tag
        color="orange"
        style={{ minWidth: 48, textAlign: "center", fontSize: 13 }}
      >
        {v ?? 0}
      </Tag>
    ),
  },
  {
    title: "Available",
    key: "available",
    align: "center",
    render: (_, row) => {
      const available =
        row.total_quantity !== null && row.total_quantity !== undefined
          ? row.total_quantity -
            (row.used_quantity ?? 0) -
            (row.reserved_quantity ?? 0)
          : null;
      const isLow =
        available !== null && available !== undefined && available <= 10;
      return (
        <Tag
          color={available === 0 ? "red" : isLow ? "volcano" : "default"}
          style={{ minWidth: 48, textAlign: "center", fontSize: 13 }}
        >
          {available ?? "—"}
        </Tag>
      );
    },
  },
  {
    title: "Venue",
    dataIndex: "location_name",
    key: "location_name",
    align:"center",
    render: (v) => v ?? <span style={{ color: "#bbb" }}>—</span>,
  },
];

function AnalyticsTab({ eventId, isPublished }) {
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(false);

  async function fetchInventory() {
    setLoading(true);
    const { data, errors } = await getEventInventory(eventId);
    setLoading(false);
    if (errors?.length) {
      renderErrorNotifications(errors);
      return;
    }
    setInventory(data ?? []);
  }

  useEffect(() => {
    if (eventId && isPublished) fetchInventory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, isPublished]);

  if (!isPublished) {
    return (
      <div className="rounded-2xl border bg-card shadow-sm">
        <div className="p-12 text-center text-muted-foreground space-y-4">
          <StockOutlined style={{ fontSize: 48 }} />
          <p>Inventory data is available once the event is published.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {/* <Package size={20} /> */}
        <Store className="h-4 w-4" />
        <span style={{ fontSize: 16, fontWeight: 700 }}>Ticket Inventory</span>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "48px 0" }}>
          <Spin size="large" />
        </div>
      ) : inventory.length === 0 ? (
        <Empty description="No inventory records found for this event." />
      ) : (
        <>
          {/* Summary cards */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
              gap: 16,
            }}
          >
            {[
              {
                label: "Total Tickets",
                value: inventory.reduce(
                  (s, r) => s + (r.total_quantity ?? 0),
                  0
                ),
                color: "#1677ff",
                bg: "#e6f4ff",
              },
              {
                label: "Sold",
                value: inventory.reduce(
                  (s, r) => s + (r.used_quantity ?? 0),
                  0
                ),
                color: "#52c41a",
                bg: "#f6ffed",
              },
              {
                label: "Reserved",
                value: inventory.reduce(
                  (s, r) => s + (r.reserved_quantity ?? 0),
                  0
                ),
                color: "#fa8c16",
                bg: "#fff7e6",
              },
              {
                label: "Available",
                value: inventory.reduce(
                  (s, r) =>
                    s +
                    Math.max(
                      0,
                      (r.total_quantity ?? 0) -
                        (r.used_quantity ?? 0) -
                        (r.reserved_quantity ?? 0)
                    ),
                  0
                ),
                color: "#722ed1",
                bg: "#f9f0ff",
              },
            ].map(({ label, value, color, bg }) => (
              <div
                key={label}
                style={{
                  background: bg,
                  border: `1px solid ${color}22`,
                  borderRadius: 12,
                  padding: "16px 20px",
                }}
              >
                <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>
                  {label}
                </div>
                <div style={{ fontSize: 28, fontWeight: 800, color }}>
                  {value}
                </div>
              </div>
            ))}
          </div>

          {/* Detail table */}
          <Table
            rowKey="product_id"
            columns={columns}
            dataSource={inventory}
            pagination={false}
            size="middle"
            style={{ borderRadius: 12, overflow: "hidden" }}
          />
        </>
      )}
    </div>
  );
}

export default AnalyticsTab;
